import React from 'react';
import { motion } from 'framer-motion';

const PrivacyPolicy: React.FC = () => {
  return (
    <motion.div
      className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 transition-colors duration-200"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 transition-colors duration-200">
          {/* 页面标题 */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">隐私政策</h1>
            <p className="text-gray-600 dark:text-gray-300">AI咖 Privacy Policy</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">最后更新时间：2025年6月</p>
          </div>

          {/* 政策内容 */}
          <div className="prose prose-gray max-w-none">
            
            {/* 前言 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">前言</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                欢迎使用AI咖（以下简称"本服务"）。我们深知个人信息对您的重要性，并会尽全力保护您的个人信息安全可靠。我们致力于维持您对我们的信任，恪守以下原则，保护您的个人信息：
              </p>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
                <li>权责一致原则：我们只会处理实现产品功能所必要的信息</li>
                <li>目的明确原则：我们明确告知您收集信息的目的和用途</li>
                <li>选择同意原则：我们充分尊重您的选择权</li>
                <li>最少够用原则：我们只收集与产品功能相关的必要信息</li>
                <li>确保安全原则：我们采用业界成熟的安全保护措施</li>
                <li>主体参与原则：我们充分保障您的合法权益</li>
                <li>公开透明原则：我们努力确保信息处理的透明度</li>
              </ul>
            </section>

            {/* 信息收集 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">1. 我们收集的信息</h2>
              
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">1.1 您主动提供的信息</h3>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                <li>账户信息：用户名、密码（加密存储）</li>
                <li>提示词内容：您创建、编辑和保存的AI提示词</li>
                <li>标签和分类：您为提示词设置的标签和分类信息</li>
                <li>联系信息：如您主动提供的反馈邮箱等</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">1.2 自动收集的信息</h3>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                <li>设备信息：设备型号、操作系统版本、浏览器类型</li>
                <li>日志信息：访问时间、IP地址、访问页面</li>
                <li>使用情况：功能使用频率、操作记录（仅用于改进服务）</li>
                <li>技术信息：Cookie、本地存储等技术标识符</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">1.3 浏览器扩展特殊说明</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                我们的Chrome浏览器扩展可能收集以下信息：
              </p>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
                <li>当前活动标签页的URL（仅在您主动保存提示词时）</li>
                <li>您选中的文本内容（仅在您主动保存时）</li>
                <li>扩展使用统计数据（匿名化处理）</li>
              </ul>
            </section>

            {/* 信息使用 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">2. 我们如何使用信息</h2>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
                <li>提供、维护和改进我们的服务</li>
                <li>验证您的身份并保护账户安全</li>
                <li>存储和管理您的提示词内容</li>
                <li>分析服务使用情况以改进用户体验</li>
                <li>响应您的客户服务请求</li>
                <li>发送重要的服务通知</li>
                <li>检测、预防和解决技术问题</li>
                <li>遵守法律法规要求</li>
              </ul>
            </section>

            {/* 信息共享 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">3. 信息共享、转让、公开披露</h2>
              
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">3.1 共享</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                我们不会与任何公司、组织和个人共享您的个人信息，但以下情况除外：
              </p>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                <li>事先获得您的明确同意</li>
                <li>法律法规规定的情况</li>
                <li>与我们的关联公司共享（仅限于实现产品功能所必需）</li>
                <li>与授权合作伙伴共享（严格限制用途并要求保密）</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">3.2 转让</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                我们不会将您的个人信息转让给任何公司、组织和个人，但以下情况除外：
              </p>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                <li>事先获得您的明确同意</li>
                <li>在涉及合并、收购或破产清算时，如涉及到个人信息转让，我们会要求新的持有您个人信息的公司、组织继续受此隐私政策的约束</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">3.3 公开披露</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                我们仅会在以下情况下公开披露您的个人信息：
              </p>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
                <li>获得您明确同意后</li>
                <li>基于法律的披露：在法律、法律程序、诉讼或政府主管部门强制性要求的情况下</li>
              </ul>
            </section>

            {/* 信息保护 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">4. 我们如何保护您的信息</h2>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
                <li>数据加密：使用行业标准的加密技术保护数据传输和存储</li>
                <li>访问控制：严格限制员工对个人信息的访问权限</li>
                <li>安全审计：定期进行安全漏洞检测和修复</li>
                <li>事件响应：建立数据安全事件应急响应机制</li>
                <li>备份保护：定期备份重要数据并确保备份安全</li>
                <li>物理安全：服务器托管在具有严格安全措施的数据中心</li>
              </ul>
            </section>

            {/* 您的权利 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">5. 您的权利</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">按照中国相关的法律、法规、标准，我们保障您对自己的个人信息行使以下权利：</p>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
                <li>访问权：您有权访问您的个人信息</li>
                <li>更正权：您有权更正错误或不完整的个人信息</li>
                <li>删除权：您有权要求我们删除您的个人信息</li>
                <li>撤回同意：您有权撤回您之前给予的同意</li>
                <li>投诉举报：您有权向监管机构投诉举报</li>
                <li>数据可携带权：您有权获取您的个人信息副本</li>
              </ul>
            </section>

            {/* Cookie使用 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">6. Cookie和类似技术</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                我们使用Cookie和类似技术来改善您的用户体验：
              </p>
              <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
                <li>身份验证：记住您的登录状态</li>
                <li>偏好设置：保存您的个性化设置</li>
                <li>安全保护：检测异常登录行为</li>
                <li>性能优化：提高页面加载速度</li>
                <li>使用分析：了解服务使用情况（匿名化数据）</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mt-4">
                您可以通过浏览器设置管理Cookie，但这可能影响某些功能的正常使用。
              </p>
            </section>

            {/* 未成年人保护 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">7. 未成年人保护</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                我们非常重视对未成年人个人信息的保护。如果您是18周岁以下的未成年人，在使用我们的服务前，应事先取得您的家长或法定监护人的同意。如果您是未成年人的监护人，当您对您所监护的未成年人的个人信息有相关疑问时，请通过本政策公布的联系方式与我们联系。
              </p>
            </section>

            {/* 政策更新 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">8. 隐私政策的更新</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                我们可能适时修订本政策的条款，该等修订构成本政策的一部分。如该等修订造成您在本政策下权利的实质减少，我们将在修订生效前通过在主页上显著位置提示或向您发送电子邮件或以其他方式通知您。在该种情况下，若您继续使用我们的服务，即表示同意受经修订的本政策的约束。
              </p>
            </section>

            {/* 联系我们 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">9. 如何联系我们</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                如果您对本隐私政策有任何疑问、意见或建议，通过以下方式与我们联系：
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <ul className="text-gray-700 dark:text-gray-300 space-y-2">
                  <li><strong>小红书：</strong> 朝朝AI工坊</li>
                  <li><strong>抖音：</strong> 朝朝AI工坊</li>
                  <li><strong>微信：</strong> 扫描页面底部二维码添加</li>
                  <li><strong>响应时间：</strong> 我们将在24小时内回复您的请求</li>
                </ul>
              </div>
            </section>

            {/* 生效日期 */}
            <section className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <p className="text-sm text-gray-500 text-center">
                本隐私政策于2025年6月生效。本政策的解释权归AI咖所有。
              </p>
            </section>

          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PrivacyPolicy; 